import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useOrder } from '../context/OrderContext'
import { useAuth } from '../context/AuthContext'
import Stepper from "../components/Stepper"
import OrderChat from '../components/OrderChat'
import { markMessagesAsRead } from '../lib/chatService'

// ---------------------- Types ----------------------
type OrderData = {
  id: string
  created_at: string
  total_price: number
  status: string
  racket_brand: string
  racket_description?: string | null
  racket_owner_name?: string | null
  tension_vertical?: number | null
  tension_horizontal?: number | null
  pickup_address?: string | null
  delivery_address?: string | null
  pickup_distance_km?: number | null
  delivery_distance_km?: number | null

  service: { name: string; base_price: number } | null
  pickup_mode: { label: string } | null
  delivery_mode: { label: string } | null

  pickup_price?: number | null
  delivery_price?: number | null
  is_partner_booking?: boolean

  // Coupon
  coupon_id?: string | null
  original_service_price?: number | null
  user_discount_amount?: number | null
  partner_commission_amount?: number | null
  coupon?: { code: string; type: string } | null

  options: { label: string; price_delta: number }[]
}

type RawOrderData = {
  id: string
  created_at: string
  total_price: number
  status: string
  racket_brand: string
  racket_description?: string | null
  racket_owner_name?: string | null
  tension_vertical?: number | null
  tension_horizontal?: number | null
  pickup_address?: string | null
  delivery_address?: string | null
  pickup_distance_km?: number | null
  delivery_distance_km?: number | null

  pickup_price?: number | null
  delivery_price?: number | null
  is_partner_booking?: boolean

  // Coupon
  coupon_id?: string | null
  original_service_price?: number | null
  user_discount_amount?: number | null
  partner_commission_amount?: number | null
  coupon: { code: string; type: string } | null

  service: { name: string; base_price: number } | null
  pickup_mode: { label: string } | null
  delivery_mode: { label: string } | null
  order_options: { option: { label: string; price_delta: number } | null }[]
}

const STARS = [1, 2, 3, 4, 5]

// ---------------------- Component ----------------------
const OrderConfirmationPage: React.FC = () => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { resetOrder } = useOrder()
  const { profile } = useAuth()

  const [order, setOrder] = useState<OrderData | null>(null)
  const [loading, setLoading] = useState(true)

  // Feedback
  const [feedbackRating, setFeedbackRating] = useState(0)
  const [feedbackHovered, setFeedbackHovered] = useState(0)
  const [feedbackComment, setFeedbackComment] = useState('')
  const [feedbackName, setFeedbackName] = useState('')
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false)
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)
  const [feedbackAlreadyDone, setFeedbackAlreadyDone] = useState(false)

  const orderId = params.get('orderId')

  useEffect(() => {
    if (!order || !profile?.id) return
    markMessagesAsRead(order.id, profile.id)
  }, [order?.id])

  // Pré-remplir le nom depuis le profil
  useEffect(() => {
    if (profile?.full_name && !feedbackName) setFeedbackName(profile.full_name)
  }, [profile])

  // Vérifier si un feedback existe déjà pour cette commande
  useEffect(() => {
    if (!orderId || order?.status !== 'LIVREE') return
    supabase
      .from('order_feedbacks')
      .select('id')
      .eq('order_id', orderId)
      .maybeSingle()
      .then(({ data }) => { if (data) setFeedbackAlreadyDone(true) })
  }, [orderId, order?.status])

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orderId || feedbackRating === 0 || !feedbackName.trim()) return
    setFeedbackSubmitting(true)
    const { error } = await supabase.from('order_feedbacks').insert({
      order_id: orderId,
      rating: feedbackRating,
      comment: feedbackComment.trim() || null,
      reviewer_name: feedbackName.trim(),
    })
    setFeedbackSubmitting(false)
    if (error?.code === '23505') { setFeedbackAlreadyDone(true); return }
    if (!error) setFeedbackSubmitted(true)
  }
  
    
  useEffect(() => {
    if (!orderId) {
      navigate('/order/start')
      return
    }

    const loadOrder = async () => {
      setLoading(true)

      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          created_at,
          total_price,
          status,
          racket_brand,
          racket_description,
          racket_owner_name,
          tension_vertical,
          tension_horizontal,
          pickup_address,
          delivery_address,
          pickup_distance_km,
          delivery_distance_km,
          pickup_price,
          delivery_price,
          is_partner_booking,

          coupon_id,
          original_service_price,
          user_discount_amount,
          partner_commission_amount,
          coupon:coupons (code, type),

          service:services!fk_orders_service (name, base_price),

          pickup_mode:transport_modes!fk_orders_pickup_mode_id (label),
          delivery_mode:transport_modes!fk_orders_delivery_mode_id (label),

          order_options:order_options (
            option:service_options (label, price_delta)
          )
        `)
        .eq('id', orderId)
        .maybeSingle<RawOrderData>()

      if (error || !data) {
        console.error("Erreur chargement commande:", error)
        setOrder(null)
        setLoading(false)
        return
      }

      const mapped: OrderData = {
        id: data.id,
        created_at: data.created_at,
        total_price: Number(data.total_price),
        status: data.status,
        racket_brand: data.racket_brand,
        racket_description: data.racket_description ?? null,
        racket_owner_name: data.racket_owner_name ?? null,
        tension_vertical: data.tension_vertical ?? null,
        tension_horizontal: data.tension_horizontal ?? null,
        pickup_address: data.pickup_address ?? null,
        delivery_address: data.delivery_address ?? null,
        pickup_distance_km: data.pickup_distance_km ?? null,
        delivery_distance_km: data.delivery_distance_km ?? null,
        pickup_price: data.pickup_price ?? null,
        delivery_price: data.delivery_price ?? null,
        is_partner_booking: data.is_partner_booking ?? false,

        coupon_id: data.coupon_id ?? null,
        original_service_price: data.original_service_price != null ? Number(data.original_service_price) : null,
        user_discount_amount: data.user_discount_amount != null ? Number(data.user_discount_amount) : null,
        partner_commission_amount: data.partner_commission_amount != null ? Number(data.partner_commission_amount) : null,
        coupon: data.coupon ?? null,

        service: data.service,
        pickup_mode: data.pickup_mode ?? null,
        delivery_mode: data.delivery_mode ?? null,

        options:
          data.order_options
            ?.filter(o => o.option !== null)
            .map(o => ({
              label: o.option!.label,
              price_delta: Number(o.option!.price_delta)
            })) ?? []
      }
      setOrder(mapped)
      setLoading(false)
      resetOrder()
    }

    void loadOrder()
  }, [orderId, navigate])

  if (loading) return <p>Chargement de la commande...</p>

  if (!order) {
    return (
      <div className="card">
        <Stepper />
        <h1 className="card-title">Commande introuvable</h1>
        <button className="btn btn-primary" onClick={() => navigate('/order/start')}>
          Retour à l'accueil
        </button>
      </div>
    )
  }

  // -------- Print handler --------
  const handlePrint = () => {
    window.print()
  }

  // ------------- UI -------------
  return (
    <div id="order-confirmation" className="card" style={{ maxWidth: 700, margin: "2rem auto" }}>
      <h1 className="card-title" style={{ color: "#22c55e", textAlign: "center" }}>
        ✔ Commande confirmée
      </h1>

      <p style={{ textAlign: "center", color: "#9ca3af" }}>
        Merci {profile?.full_name} !
      </p>

      {/* SECTION - Détails de la commande */}
      <h2 style={{ marginTop: "1.5rem" }}>📦 Détails de la commande</h2>
      <ul style={{ lineHeight: 1.7 }}>
        <li><strong>ID :</strong> {order.id}</li>
        <li><strong>Date :</strong> {new Date(order.created_at).toLocaleString('fr-CH')}</li>
        <li><strong>Statut :</strong> {order.status}</li>
      </ul>

      {/* Service */}
      <h2 style={{ marginTop: "1.5rem" }}>🎾 Service</h2>
      <ul style={{ lineHeight: 1.7 }}>
        <li>
          <strong>Service :</strong> {order.service?.name}{' '}
          {order.coupon_id && order.original_service_price != null && order.user_discount_amount ? (
            <>
              (<s style={{ color: '#9ca3af' }}>{order.original_service_price.toFixed(2)} CHF</s>{' '}
              <span style={{ color: '#22c55e', fontWeight: 600 }}>{(order.original_service_price - order.user_discount_amount).toFixed(2)} CHF</span>)
            </>
          ) : (
            <>({order.service?.base_price} CHF)</>
          )}
        </li>

        {order.options.map((opt, i) => (
          <li key={i}>
            <strong>Option :</strong> {opt.label} (+{opt.price_delta} CHF)
          </li>
        ))}

        {order.racket_owner_name && (
          <li><strong>Propriétaire :</strong> {order.racket_owner_name}</li>
        )}
        <li><strong>Raquette :</strong> {order.racket_brand}</li>
        {order.racket_description && (
          <li><strong>Description :</strong> {order.racket_description}</li>
        )}

        <li>
          <strong>Tension :</strong> {order.tension_vertical} / {order.tension_horizontal} kg
        </li>
      </ul>

      {/* Pickup */}
      <h2 style={{ marginTop: "1.5rem" }}>🚗 Prise en charge</h2>
      <ul style={{ lineHeight: 1.7 }}>
        <li><strong>Mode :</strong> {order.pickup_mode?.label}</li>
        {order.pickup_address && (
          <li><strong>Adresse :</strong> {order.pickup_address}</li>
        )}
        {order.pickup_distance_km != null && (
          <li><strong>Distance :</strong> {order.pickup_distance_km.toFixed(1)} km</li>
        )}
        {order.pickup_price != null && (
          <li><strong>Prix prise en charge :</strong> {order.pickup_price.toFixed(2)} CHF</li>
        )}
      </ul>

      {/* Livraison */}
      <h2 style={{ marginTop: "1.5rem" }}>📦 Livraison</h2>
      <ul style={{ lineHeight: 1.7 }}>
        <li><strong>Mode :</strong> {order.delivery_mode?.label}</li>
        {order.delivery_address && (
          <li><strong>Adresse :</strong> {order.delivery_address}</li>
        )}
        {order.delivery_distance_km != null && (
          <li><strong>Distance :</strong> {order.delivery_distance_km.toFixed(1)} km</li>
        )}
        {order.delivery_price != null && (
          <li><strong>Prix livraison :</strong> {order.delivery_price.toFixed(2)} CHF</li>
        )}
      </ul>

      {/* Coupon / Réduction */}
      {order.coupon_id && order.coupon && (
        <div
          data-testid="order-confirmation-coupon"
          style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: 'rgba(34, 197, 94, 0.08)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '0.5rem',
          }}
        >
          <div style={{ fontWeight: 600, color: '#22c55e', marginBottom: '0.5rem' }}>
            🎟️ Code promo appliqué : {order.coupon.code}
          </div>
          {order.user_discount_amount != null && order.user_discount_amount > 0 && (
            <ul style={{ fontSize: '.9rem', margin: 0, paddingLeft: '1rem', lineHeight: 1.7 }}>
              <li>
                Prix du service original : <s>{order.original_service_price?.toFixed(2)} CHF</s>
              </li>
              <li>
                Réduction : <strong style={{ color: '#22c55e' }}>-{order.user_discount_amount.toFixed(2)} CHF</strong>
                {order.original_service_price != null && order.original_service_price > 0 && (
                  <> ({((order.user_discount_amount / order.original_service_price) * 100).toFixed(0)}%)</>
                )}
              </li>
              <li>
                Prix du service après réduction : <strong>{(order.original_service_price! - order.user_discount_amount).toFixed(2)} CHF</strong>
              </li>
            </ul>
          )}
        </div>
      )}

      {/* Total */}
      <h2
        data-testid="order-confirmation-total"
        style={{
          marginTop: "2rem",
          paddingTop: "1rem",
          borderTop: '1px solid var(--tun-hr)',
          fontSize: "1.3rem",
          color: "#22c55e",
          fontWeight: 700
        }}
      >
        💰 Total payé : {order.total_price.toFixed(2)} CHF
      </h2>

      {/* Prix avant/après si réduction */}
      {order.coupon_id && order.user_discount_amount != null && order.user_discount_amount > 0 && order.original_service_price != null && (
        <div data-testid="order-confirmation-price-before-after" style={{ fontSize: '.85rem', color: '#9ca3af', marginTop: '0.25rem' }}>
          <s>{(order.original_service_price + (order.options.reduce((s, o) => s + o.price_delta, 0)) + (order.pickup_price ?? 0) + (order.delivery_price ?? 0)).toFixed(2)} CHF</s>
          {' → '}{order.total_price.toFixed(2)} CHF
          {' '}(économie de {order.user_discount_amount.toFixed(2)} CHF)
        </div>
      )}
      {/* --- FEEDBACK (commandes livrées uniquement) --- */}
      {order.status === 'LIVREE' && (
        <section
          style={{
            marginTop: '2rem',
            padding: '1.25rem',
            background: 'rgba(34,197,94,0.06)',
            border: '1px solid rgba(34,197,94,0.2)',
            borderRadius: '.75rem',
          }}
        >
          <h2 style={{ fontSize: '1.1rem', marginBottom: '.75rem' }}>⭐ Votre avis</h2>

          {feedbackAlreadyDone || feedbackSubmitted ? (
            <p style={{ color: '#22c55e' }}>
              {feedbackSubmitted ? '🎉 Merci pour votre avis !' : '✅ Vous avez déjà laissé un avis pour cette commande.'}
            </p>
          ) : (
            <form onSubmit={handleFeedbackSubmit}>
              {/* Étoiles */}
              <div style={{ marginBottom: '1rem' }}>
                <div
                  style={{ display: 'flex', gap: '.2rem', fontSize: '2rem', cursor: 'pointer' }}
                  onMouseLeave={() => setFeedbackHovered(0)}
                >
                  {STARS.map(star => (
                    <span
                      key={star}
                      onClick={() => setFeedbackRating(star)}
                      onMouseEnter={() => setFeedbackHovered(star)}
                      style={{
                        color: star <= (feedbackHovered || feedbackRating) ? '#f59e0b' : '#334155',
                        transition: 'color .1s',
                        userSelect: 'none',
                      }}
                    >★</span>
                  ))}
                </div>
                {feedbackRating === 0 && (
                  <p style={{ fontSize: '.8rem', color: '#64748b', marginTop: '.25rem' }}>
                    Cliquez sur une étoile pour noter
                  </p>
                )}
              </div>

              {/* Nom */}
              <div style={{ marginBottom: '.75rem' }}>
                <label style={{ display: 'block', fontSize: '.9rem', fontWeight: 600, marginBottom: '.25rem' }}>
                  Votre nom *
                </label>
                <input
                  className="input"
                  value={feedbackName}
                  onChange={e => setFeedbackName(e.target.value)}
                  placeholder="Prénom Nom"
                  required
                />
              </div>

              {/* Commentaire */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '.9rem', fontWeight: 600, marginBottom: '.25rem' }}>
                  Commentaire <span style={{ color: '#64748b', fontWeight: 400 }}>(optionnel)</span>
                </label>
                <textarea
                  className="input"
                  value={feedbackComment}
                  onChange={e => setFeedbackComment(e.target.value)}
                  rows={3}
                  placeholder="Qualité du cordage, délai, accueil…"
                  style={{ resize: 'vertical', width: '100%', boxSizing: 'border-box' }}
                />
              </div>

              <button
                className="btn btn-primary"
                type="submit"
                disabled={feedbackSubmitting || feedbackRating === 0 || !feedbackName.trim()}
              >
                {feedbackSubmitting ? 'Envoi…' : 'Envoyer mon avis'}
              </button>
            </form>
          )}
        </section>
      )}

      {/* --- CHAT COMMANDE (masqué pour les réservations partenaires) --- */}
      {!order.is_partner_booking && (
        <div style={{ marginTop: '2rem' }}>
          <OrderChat orderId={order.id} orderStatus={order.status} />
        </div>
      )}
      {/* Actions */}
      <div style={{ marginTop: "2rem", textAlign: "center" }}>
        <button data-testid="order-confirmation-view-orders" className="btn btn-primary" onClick={() => navigate(order.is_partner_booking ? '/partner/orders' : '/orders')}>
          Voir mes commandes
        </button>

        <button data-testid="order-confirmation-print" className="btn btn-ghost" onClick={handlePrint} style={{ marginLeft: ".5rem" }}>
          🖨️ Imprimer
        </button>
      </div>
    </div>
  )
}

export default OrderConfirmationPage
